import React, { useState, useEffect, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './App.css';
import { Menu, X, Monitor, Cpu, Server, Shield, Mail, Phone, MapPin, ChevronRight, CheckCircle, Network, Code, FileText, HardDrive, PhoneCall, Brain, Wifi, Globe, Download, Printer, Layers, Clock } from 'lucide-react';

function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({
    necessari: true,
    funzionali: false,
    statistiche: false,
    terzeParti: false,
  });

  useEffect(() => {
    if (!localStorage.getItem('cookieConsent')) setVisible(true);
  }, []);

  const save = (consent) => {
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setVisible(false);
  };

  const acceptAll = () => save({ necessari: true, funzionali: true, statistiche: true, terzeParti: true });
  const rejectAll = () => save({ necessari: true, funzionali: false, statistiche: false, terzeParti: false });
  const saveCustom = () => save(prefs);

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t-2 border-orange-500 shadow-2xl p-6">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-start justify-between mb-3">
          <h3 className="text-lg font-bold text-gray-800">Informativa sui Cookie</h3>
          <button onClick={rejectAll} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Questo sito utilizza cookie e tecnologie simili per migliorare l'esperienza di navigazione e per finalità
          statistiche e di marketing. Puoi accettare tutti i cookie, rifiutarli o personalizzare le tue preferenze.
          Per maggiori informazioni consulta la nostra <span className="text-orange-600 underline cursor-pointer">Cookie Policy</span>.
        </p>

        {showDetails && (
          <div className="bg-gray-50 rounded-lg p-4 mb-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">Cookie Necessari</p>
                <p className="text-xs text-gray-500">Indispensabili per il funzionamento del sito. Non possono essere disattivati.</p>
              </div>
              <span className="text-xs bg-gray-300 text-gray-600 px-2 py-1 rounded">Sempre attivi</span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">Cookie Funzionali</p>
                <p className="text-xs text-gray-500">Permettono funzionalità avanzate come l'invio del modulo di contatto (EmailJS).</p>
              </div>
              <input type="checkbox" checked={prefs.funzionali} onChange={e => setPrefs({...prefs, funzionali: e.target.checked})} className="w-5 h-5 accent-orange-600" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">Cookie Statistici</p>
                <p className="text-xs text-gray-500">Ci aiutano a capire come i visitatori interagiscono con il sito.</p>
              </div>
              <input type="checkbox" checked={prefs.statistiche} onChange={e => setPrefs({...prefs, statistiche: e.target.checked})} className="w-5 h-5 accent-orange-600" />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">Cookie di Terze Parti</p>
                <p className="text-xs text-gray-500">Utilizzati da Google Maps e altri servizi esterni integrati nel sito.</p>
              </div>
              <input type="checkbox" checked={prefs.terzeParti} onChange={e => setPrefs({...prefs, terzeParti: e.target.checked})} className="w-5 h-5 accent-orange-600" />
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button onClick={acceptAll} className="bg-orange-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-orange-700 transition">
            Accetta Tutti
          </button>
          <button onClick={rejectAll} className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg text-sm font-semibold hover:bg-gray-300 transition">
            Rifiuta Tutti
          </button>
          {showDetails ? (
            <button onClick={saveCustom} className="border-2 border-orange-600 text-orange-600 px-5 py-2 rounded-lg text-sm font-semibold hover:bg-orange-50 transition">
              Salva Preferenze
            </button>
          ) : (
            <button onClick={() => setShowDetails(true)} className="border-2 border-orange-600 text-orange-600 px-5 py-2 rounded-lg text-sm font-semibold hover:bg-orange-50 transition">
              Personalizza
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ComputerlandiWebsite() {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('idle'); // idle | sending | success | error
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedService, setSelectedService] = useState(null);

  const features = [
    { title: 'Esperienza Ventennale', text: 'Oltre 20 anni di esperienza nel settore IT, al servizio delle aziende.' },
    { title: 'Servizio Clienti Dedicato', text: 'Team di supporto professionale a tua disposizione negli orari lavorativi.' },
    { title: 'Soluzioni Su Misura', text: 'Ogni progetto è personalizzato sulle reali esigenze della tua azienda.' },
    { title: 'Unico Fornitore, Tutti i Servizi', text: 'Seguiamo le aziende dalla A alla Z. Un unico partner per ogni esigenza IT.' },
  ];

  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage, selectedService]);

  useEffect(() => {
    const timer = setInterval(() => {
      setIsTransitioning(true);
      setCurrentSlide(prev => prev + 1);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (currentSlide === features.length) {
      setTimeout(() => {
        setIsTransitioning(false);
        setCurrentSlide(0);
      }, 2500);
    }
  }, [currentSlide]);

  const EMAILJS_SERVICE_ID  = 'service_ppzdu6p';
  const EMAILJS_TEMPLATE_ID = 'template_qoe42yn';
  const EMAILJS_PUBLIC_KEY  = 'Gas5dyJikfp7uT6jG';

  const navigation = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'Chi Siamo' },
    { id: 'services', label: 'Servizi' },
    { id: 'support', label: 'Assistenza Remota' },
    { id: 'contact', label: 'Contatti' }
  ];

  const services = [
    {
      icon: <Monitor className="w-12 h-12" />,
      title: 'Soluzioni IT Aziendali',
      description: 'Progettazione e implementazione di infrastrutture IT su misura per la tua azienda.',
      image: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=600&q=80&fit=crop',
      overlay: 10,
      details: {
        intro: 'Computerlandi progetta e implementa infrastrutture IT complete e personalizzate per aziende di ogni dimensione nel territorio di Altivole e provincia di Treviso. Ogni soluzione viene studiata in base alle reali esigenze operative del cliente, garantendo scalabilità, affidabilità e massima efficienza. Siamo il partner tecnologico unico che segue l\'azienda dalla fase di analisi fino all\'assistenza post-implementazione.',
        features: [
          'Analisi approfondita delle esigenze e dell\'infrastruttura esistente',
          'Progettazione di sistemi IT scalabili e su misura',
          'Fornitura e installazione di hardware e software professionale',
          'Integrazione con i sistemi gestionali già in uso in azienda',
          'Formazione del personale sui nuovi strumenti adottati',
          'Manutenzione preventiva e monitoraggio proattivo dell\'infrastruttura'
        ],
        benefits: [
          'Riduzione dei tempi di inattività grazie a infrastrutture stabili e monitorate',
          'Ottimizzazione dei costi IT con soluzioni dimensionate alle reali necessità',
          'Un unico interlocutore per tutta la gestione tecnologica aziendale',
          'Maggiore produttività del personale con strumenti IT sempre efficienti'
        ]
      }
    },
    {
      icon: <Server className="w-12 h-12" />,
      title: 'Server e Cloud',
      description: 'Gestione server, migrazione cloud e soluzioni di backup professionali.',
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&q=80&fit=crop',
      overlay: 0,
      details: {
        intro: 'Offriamo soluzioni server complete, dalla configurazione di server fisici in sede alla migrazione verso ambienti cloud ibridi o totalmente in cloud. Il nostro team certifica le architetture più adatte alle esigenze di ogni azienda, garantendo continuità operativa, prestazioni elevate e sicurezza dei dati. Gestiamo ambienti Microsoft Azure, AWS, e soluzioni on-premise con le principali piattaforme enterprise.',
        features: [
          'Configurazione e gestione di server fisici e virtuali',
          'Migrazione verso ambienti cloud (Microsoft Azure, AWS, Google Cloud)',
          'Implementazione di soluzioni ibride on-premise e cloud',
          'Monitoraggio 24/7 delle risorse server e dei servizi critici',
          'Aggiornamento e patching dei sistemi operativi server',
          'Ottimizzazione delle prestazioni e della capacità di storage'
        ],
        benefits: [
          'Continuità operativa garantita con architetture ridondanti e alta disponibilità',
          'Scalabilità delle risorse in base alla crescita dell\'azienda',
          'Riduzione dei costi di gestione con ambienti cloud ottimizzati',
          'Accesso sicuro alle risorse aziendali da qualsiasi luogo e dispositivo'
        ]
      }
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Cybersecurity',
      description: 'Protezione dati, firewall, antivirus e consulenza sulla sicurezza informatica.',
      image: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?w=600&q=80&fit=crop',
      details: {
        intro: 'La sicurezza informatica è oggi una priorità assoluta per qualsiasi azienda. Computerlandi offre soluzioni di cybersecurity complete per proteggere i dati aziendali, le comunicazioni e l\'infrastruttura IT da minacce sempre più sofisticate. Dalla configurazione di firewall enterprise all\'implementazione di policy di sicurezza avanzate, affianchiamo le aziende nel percorso verso un ambiente IT sicuro e conforme alle normative vigenti.',
        features: [
          'Installazione e gestione di firewall enterprise (Draytek, Fortinet, SonicWall, pfSense)',
          'Protezione endpoint con soluzioni antivirus e antimalware di nuova generazione',
          'Audit di sicurezza e vulnerability assessment dell\'infrastruttura IT',
          'Implementazione di VPN sicure per l\'accesso remoto dei dipendenti',
          'Formazione del personale sulla sicurezza informatica e phishing awareness',
          'Monitoraggio continuo delle minacce e risposta agli incidenti di sicurezza'
        ],
        benefits: [
          'Protezione completa dei dati aziendali e dei dati sensibili dei clienti',
          'Conformità alle normative GDPR e alle policy di sicurezza del settore',
          'Riduzione del rischio di attacchi ransomware e violazioni dei dati',
          'Continuità operativa garantita anche in caso di tentativi di intrusione'
        ]
      }
    },
    {
      icon: <Cpu className="w-12 h-12" />,
      title: 'Assistenza Tecnica',
      description: 'Supporto tecnico continuativo, manutenzione e risoluzione problemi hardware/software.',
      image: 'https://images.unsplash.com/photo-1537432376769-00f5c2f4c8d2?w=600&q=80&fit=crop',
      details: {
        intro: 'Il team tecnico di Computerlandi garantisce assistenza professionale e tempestiva per risolvere qualsiasi problematica hardware e software che possa rallentare l\'operatività aziendale. Operiamo sia in sede presso il cliente nel territorio di Altivole e provincia di Treviso, sia da remoto tramite strumenti di accesso sicuro, assicurando interventi rapidi ed efficaci. Il nostro obiettivo è minimizzare i tempi di fermo e mantenere l\'infrastruttura IT sempre in perfetta efficienza.',
        features: [
          'Assistenza on-site e remota per problemi hardware e software',
          'Manutenzione preventiva programmata di workstation e server',
          'Diagnosi e risoluzione di guasti hardware con sostituzione componenti',
          'Installazione, configurazione e aggiornamento di sistemi operativi e software',
          'Supporto per stampanti, periferiche e dispositivi di rete',
          'Gestione contratti di assistenza personalizzati con SLA definiti',
          'Servizio disponibile anche il sabato mattina per massima flessibilità'
        ],
        benefits: [
          'Tempi di risposta rapidi e garanzia di risoluzione delle problematiche',
          'Riduzione dei fermi macchina e dell\'impatto sulla produttività aziendale',
          'Unico punto di contatto per tutta l\'assistenza IT dell\'azienda',
          'Manutenzione preventiva che riduce i guasti improvvisi e i costi di emergenza'
        ]
      }
    },
    {
      icon: <Network className="w-12 h-12" />,
      title: 'Networking e Infrastrutture',
      description: 'Progettazione e gestione reti aziendali, cablaggio strutturato e ottimizzazione connettività.',
      image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80&fit=crop',
      details: {
        intro: 'Una rete aziendale affidabile e ben strutturata è la spina dorsale di ogni attività moderna. Computerlandi progetta e gestisce infrastrutture di rete complete per aziende di ogni dimensione. Per le opere di posa e cablaggio ci avvaliamo di aziende specializzate e certificate, mentre ci occupiamo direttamente della selezione e specifica dei materiali, garantendo componenti di qualità professionale adatti alle esigenze di ogni progetto. Garantiamo reti performanti, sicure e facilmente scalabili per supportare la crescita del business.',
        features: [
          'Progettazione e realizzazione di reti LAN/WAN aziendali',
          'Cablaggio strutturato Cat6/Cat6A e installazione rack di distribuzione',
          'Configurazione di switch managed e router enterprise (Cisco, HP, Ubiquiti)',
          'Segmentazione della rete con VLAN per maggiore sicurezza e performance',
          'Implementazione di reti SD-WAN per sedi multiple e connettività distribuita',
          'Monitoraggio e ottimizzazione continua delle prestazioni di rete'
        ],
        benefits: [
          'Rete aziendale stabile e performante che supporta tutti i processi produttivi',
          'Sicurezza migliorata grazie alla corretta segmentazione e al controllo degli accessi',
          'Scalabilità semplice per aggiungere nuovi utenti, sedi o dispositivi',
          'Riduzione dei costi di gestione con infrastrutture ben documentate e monitorate'
        ]
      }
    },
    {
      icon: <Code className="w-12 h-12" />,
      title: 'Sviluppo Software/Web',
      description: 'Creazione di applicazioni personalizzate, siti web aziendali e portali gestionali.',
      image: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80&fit=crop',
      details: {
        intro: 'Computerlandi sviluppa soluzioni software e web personalizzate per rispondere alle specifiche esigenze di ogni azienda. Dal sito vetrina al portale gestionale complesso, dal software su misura all\'integrazione con sistemi ERP e CRM già esistenti, il nostro team di sviluppatori realizza applicazioni moderne, sicure e facili da utilizzare. Accompagniamo il cliente in ogni fase, dall\'analisi dei requisiti fino al rilascio e alla manutenzione del prodotto finito.',
        features: [
          'Sviluppo di siti web aziendali e portali e-commerce professionali',
          'Creazione di applicazioni gestionali personalizzate (web e desktop)',
          'Integrazione con sistemi ERP, CRM e software gestionali esistenti',
          'Sviluppo di API e servizi per la comunicazione tra sistemi diversi',
          'Ottimizzazione SEO e performance dei siti web aziendali',
          'Manutenzione, aggiornamento e supporto continuativo post-rilascio'
        ],
        benefits: [
          'Software su misura che si adatta perfettamente ai processi aziendali',
          'Automazione dei processi ripetitivi con riduzione degli errori e dei costi',
          'Presenza digitale professionale che valorizza l\'immagine dell\'azienda',
          'Integrazione fluida tra tutti i sistemi informativi aziendali'
        ]
      }
    },
    {
      icon: <FileText className="w-12 h-12" />,
      title: 'Consulenza IT',
      description: 'Analisi strategica, audit informatici e pianificazione della trasformazione digitale.',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=600&q=80&fit=crop',
      details: {
        intro: 'La consulenza IT di Computerlandi aiuta le aziende a prendere decisioni tecnologiche consapevoli e strategiche. Attraverso audit approfonditi dell\'infrastruttura esistente e una valutazione accurata degli obiettivi aziendali, definiamo roadmap di innovazione digitale concrete e sostenibili. Il nostro approccio indipendente garantisce soluzioni imparziali, orientate al reale beneficio dell\'impresa.',
        features: [
          'Audit completo dell\'infrastruttura IT e analisi dei punti critici',
          'Pianificazione strategica della trasformazione digitale aziendale',
          'Valutazione e selezione delle tecnologie più adatte agli obiettivi aziendali',
          'Analisi dei costi IT e ottimizzazione del budget tecnologico',
          'Supporto nella gestione del cambiamento e nell\'adozione di nuovi strumenti',
          'Redazione di capitolati tecnici per gare d\'appalto e forniture IT'
        ],
        benefits: [
          'Decisioni tecnologiche più consapevoli grazie a una visione esperta e indipendente',
          'Ottimizzazione degli investimenti IT con priorità allineate agli obiettivi di business',
          'Riduzione del rischio tecnologico con una roadmap chiara e strutturata',
          'Accelerazione del percorso di innovazione digitale dell\'azienda'
        ]
      }
    },
    {
      icon: <HardDrive className="w-12 h-12" />,
      title: 'Backup e Disaster Recovery',
      description: 'Backup professionale con Veeam Backup Enterprise, soluzioni di backup automatico e piani di recupero dati per la continuità operativa.',
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80&fit=crop',
      details: {
        intro: 'La perdita di dati aziendali può avere conseguenze devastanti per qualsiasi impresa. Computerlandi implementa soluzioni di backup e disaster recovery professionali basate su Veeam Backup & Replication Enterprise, garantendo la protezione completa di tutti i dati critici e la capacità di ripristino rapido in caso di guasto, attacco ransomware o qualsiasi evento imprevisto. Progettiamo piani di continuità operativa su misura per ogni realtà aziendale.',
        features: [
          'Implementazione di backup automatici con Veeam Backup Enterprise',
          'Backup su storage locale, NAS e cloud ibrido con cifratura dei dati',
          'Definizione e testing regolare dei piani di Disaster Recovery',
          'Replica dei dati in tempo reale per ambienti critici ad alta disponibilità',
          'Monitoraggio e verifica automatica dell\'integrità dei backup',
          'Ripristino rapido di singoli file, macchine virtuali o interi sistemi'
        ],
        benefits: [
          'Protezione totale dei dati aziendali contro guasti, errori umani e attacchi informatici',
          'Tempi di ripristino minimizzati grazie a procedure testate e documentate',
          'Conformità normativa con conservazione sicura e tracciabile dei dati',
          'Tranquillità operativa con la certezza che i dati siano sempre al sicuro'
        ]
      }
    },
    {
      icon: <PhoneCall className="w-12 h-12" />,
      title: 'VoIP e Telefonia',
      description: 'Centralini 3CX con AI, unified communications e sistemi telefonici cloud per aziende.',
      image: 'https://images.unsplash.com/photo-1516387938699-a93567ec168e?w=600&q=80&fit=crop',
      details: {
        intro: 'Computerlandi propone soluzioni di telefonia aziendale moderne basate sulla tecnologia VoIP, con centralini 3CX dotati di intelligenza artificiale integrata per gestire le comunicazioni in modo intelligente ed efficiente. I nostri sistemi unificano voce, video, messaggistica e collaborazione in un\'unica piattaforma, accessibile da qualsiasi dispositivo e da qualsiasi luogo, riducendo significativamente i costi delle comunicazioni aziendali.',
        features: [
          'Installazione e configurazione di centralini 3CX con funzionalità AI',
          'Integrazione con CRM e software gestionali per riconoscimento automatico dei chiamanti',
          'App mobile e desktop per lavorare da qualsiasi luogo con lo stesso numero aziendale',
          'Videoconferenze HD integrate nella piattaforma di comunicazione unificata',
          'Gestione avanzata delle code chiamate, IVR e risponditori automatici',
          'Monitoraggio e reportistica dettagliata sulle comunicazioni aziendali'
        ],
        benefits: [
          'Riduzione significativa dei costi delle comunicazioni telefoniche aziendali',
          'Flessibilità totale per il lavoro da remoto con numero aziendale sempre attivo',
          'Professionalità nelle comunicazioni con funzioni avanzate di centralino',
          'Unica piattaforma per voce, video, chat e collaborazione del team'
        ]
      }
    },
    {
      icon: <Brain className="w-12 h-12" />,
      title: 'Server Dedicati per AI in Locale',
      description: 'Infrastrutture server dedicate per machine learning, AI e elaborazione dati on-premise.',
      image: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?w=600&q=80&fit=crop',
      details: {
        intro: 'L\'intelligenza artificiale non deve necessariamente risiedere nel cloud: Computerlandi progetta e fornisce server dedicati per l\'esecuzione di modelli AI e machine learning direttamente in azienda, garantendo privacy totale dei dati, latenza minima e costi operativi prevedibili. I nostri server AI on-premise sono dimensionati sulle specifiche esigenze di elaborazione del cliente, dalla piccola azienda fino alle installazioni enterprise con GPU di ultima generazione.',
        features: [
          'Progettazione di server AI con GPU NVIDIA dedicate all\'elaborazione parallela',
          'Installazione e configurazione di piattaforme AI locali (Ollama, LM Studio, ecc.)',
          'Integrazione di modelli LLM open-source per l\'uso interno aziendale',
          'Ottimizzazione hardware per workload di machine learning e inferenza AI',
          'Configurazione di ambienti Docker e Kubernetes per applicazioni AI containerizzate',
          'Formazione e supporto per l\'adozione degli strumenti di AI in azienda'
        ],
        benefits: [
          'Dati aziendali completamente al sicuro, senza trasmissione a servizi cloud esterni',
          'Nessun costo per token o chiamate API: elaborazione illimitata con hardware proprio',
          'Latenza minima e prestazioni costanti per applicazioni AI critiche',
          'Indipendenza dai provider cloud con pieno controllo dell\'infrastruttura AI'
        ]
      }
    },
    {
      icon: <Wifi className="w-12 h-12" />,
      title: 'Connessioni Fibra per la Tua Azienda',
      description: 'Fornitura e gestione di connettività fibra ottica aziendale con i migliori brand del mercato: TIM, Fastweb, Vodafone, WindTre, Eolo e altri operatori premium.',
      image: 'https://images.unsplash.com/photo-1586772002130-b0f3daa6288b?w=600&q=80&fit=crop',
      details: {
        intro: 'Una connessione internet veloce e affidabile è oggi un requisito fondamentale per qualsiasi azienda. Computerlandi seleziona e gestisce per i propri clienti le migliori offerte di connettività fibra ottica aziendale, con i principali operatori presenti sul mercato italiano. Siamo l\'unico interlocutore per attivazione, gestione e assistenza della connettività, sollevando l\'azienda da qualsiasi rapporto diretto con l\'operatore telefonico.',
        features: [
          'Analisi della migliore soluzione di connettività in base alla sede e alle esigenze',
          'Attivazione di linee fibra con TIM, Fastweb, Vodafone, WindTre, Eolo e altri',
          'Gestione delle pratiche burocratiche e coordinamento con l\'operatore',
          'Configurazione router e apparati di rete per la nuova connettività',
          'Assistenza tecnica in caso di disservizi, con gestione dei ticket verso l\'operatore',
          'Fornitura di connettività di backup (failover) per garantire continuità operativa'
        ],
        benefits: [
          'Connettività aziendale ottimizzata per le reali esigenze di banda e affidabilità',
          'Risparmio di tempo: gestiamo tutto noi, dall\'offerta all\'attivazione e all\'assistenza',
          'Continuità operativa garantita con soluzioni di connettività ridondante',
          'Supporto tecnico locale e reattivo in caso di problemi di connessione'
        ]
      }
    },
    {
      icon: <Wifi className="w-12 h-12" />,
      title: 'Installazione WiFi e Rete Mesh',
      description: 'Progettiamo e installiamo sistemi WiFi professionali per aziende. Grazie alla tecnologia Mesh garantiamo una copertura wireless ottimale in ogni angolo, eliminando le zone morte e assicurando connessione stabile e veloce su tutta la superficie.',
      image: 'https://images.unsplash.com/photo-1645725677294-ed0843b97d5c?q=80&w=1170&auto=format&fit=crop',
      details: {
        intro: 'La copertura WiFi professionale è fondamentale per le aziende moderne, dove la mobilità dei dispositivi e la connettività pervasiva sono requisiti operativi. Computerlandi progetta e installa sistemi WiFi enterprise con tecnologia Mesh di ultima generazione, garantendo copertura totale di uffici, capannoni, aree esterne e sedi multi-piano. Utilizziamo apparati professionali di marchi leader come Ubiquiti, TP-Link Omada e Ruckus per assicurare prestazioni elevate e gestione centralizzata.',
        features: [
          'Site survey professionale per mappatura della copertura e pianificazione access point',
          'Installazione di reti WiFi Mesh enterprise con gestione centralizzata',
          'Configurazione di reti WiFi separate per dipendenti, ospiti e dispositivi IoT',
          'Ottimizzazione del roaming tra access point per dispositivi mobili',
          'Installazione in ambienti complessi: capannoni industriali, multi-piano, aree esterne',
          'Monitoraggio remoto e aggiornamento firmware centralizzato degli access point'
        ],
        benefits: [
          'Copertura WiFi totale senza zone morte in qualsiasi ambiente aziendale',
          'Connessione stabile e veloce per tutti i dispositivi mobili e workstation wireless',
          'Rete ospiti isolata per garantire la sicurezza della rete aziendale principale',
          'Gestione centralizzata semplificata con dashboard di controllo unificata'
        ]
      }
    },
    {
      icon: <Clock className="w-12 h-12" />,
      title: 'Sistemi di Timbratura Cartellini',
      description: 'Forniamo e installiamo sistemi di timbratura presenze per dipendenti: lettori di badge, impronte digitali e app mobile. Soluzione completa integrata con i principali software di rilevazione presenze e gestione del personale.',
      image: 'https://www.iclock.it/310-large_default/kit-iclock-5-misura-orari-lavoro.jpg',
      overlay: 10,
      details: {
        intro: 'Computerlandi fornisce e installa sistemi di rilevazione presenze completi per aziende di qualsiasi dimensione, dai lettori di badge ai terminali biometrici con riconoscimento delle impronte digitali. Le nostre soluzioni si integrano con i principali software di gestione del personale e paghe, automatizzando il calcolo degli orari di lavoro, degli straordinari e delle presenze. Offriamo consulenza completa per scegliere il sistema più adatto alla struttura organizzativa del cliente.',
        features: [
          'Fornitura e installazione di terminali di timbratura con badge, RFID e impronte digitali',
          'App mobile per timbratura da smartphone per personale fuori sede e smart worker',
          'Integrazione con software gestionali presenze: Zucchetti, TeamSystem, Sistemi e altri',
          'Gestione di turni, orari flessibili e pianificazione del personale',
          'Reportistica automatica su presenze, assenze, ferie e straordinari',
          'Manutenzione e assistenza tecnica dei terminali installati'
        ],
        benefits: [
          'Eliminazione degli errori nel calcolo delle presenze e degli orari di lavoro',
          'Risparmio di tempo nell\'elaborazione delle buste paga e dei report HR',
          'Conformità normativa con tracciabilità completa degli orari lavorativi',
          'Controllo in tempo reale delle presenze in azienda da qualsiasi dispositivo'
        ]
      }
    },
    {
      icon: <Globe className="w-12 h-12" />,
      title: 'Domini e Posta Aziendale',
      description: 'Registrazione e gestione domini aziendali. Posta elettronica professionale su Google Workspace o Microsoft Exchange. Trasferiamo a nostra cura tutti gli archivi email dal vecchio provider al nuovo. Registriamo anche la tua PEC aziendale.',
      image: 'https://images.unsplash.com/photo-1557200134-90327ee9fafa?w=600&q=80&fit=crop',
      details: {
        intro: 'Una casella email aziendale professionale e un dominio dedicato sono il biglietto da visita digitale di ogni impresa. Computerlandi gestisce l\'intero ciclo di vita dei domini aziendali e configura sistemi di posta elettronica professionali su piattaforme leader come Google Workspace e Microsoft 365 Exchange. Ci occupiamo anche della migrazione completa degli archivi email dal vecchio provider, senza perdita di dati e senza interruzioni operative.',
        features: [
          'Registrazione e gestione di domini .it, .com, .eu e altri TLD nazionali e internazionali',
          'Configurazione di caselle email aziendali su Google Workspace o Microsoft 365',
          'Migrazione completa degli archivi email dal vecchio provider senza perdita di dati',
          'Registrazione e gestione della PEC (Posta Elettronica Certificata) aziendale',
          'Configurazione DNS, SPF, DKIM e DMARC per massima deliverability delle email',
          'Gestione rinnovi domini e assistenza in caso di problemi di posta elettronica'
        ],
        benefits: [
          'Immagine professionale con email @nomiazienda.it su piattaforme enterprise affidabili',
          'Zero perdita di dati durante la migrazione, gestita interamente dal nostro team',
          'Conformità normativa con PEC aziendale per le comunicazioni ufficiali',
          'Continuità operativa con piattaforme email ad alta disponibilità e ridondanza'
        ]
      }
    },
    {
      icon: <Printer className="w-12 h-12" />,
      title: 'Stampanti Multifunzione a Noleggio',
      description: 'Noleggio operativo di stampanti multifunzione DEVELOP professionali per aziende. Stampa, copia, scansione e fax con canone tutto incluso: manutenzione, toner e assistenza tecnica.',
      image: 'https://www.developitalia.it/fileadmin/content/eu/DEVELOP/Products/Office_Products/ineop_450i_550i_650i/ineo_p_450i_studio_picture_11_PC-520.jpg',
      overlay: 15,
      details: {
        intro: 'Computerlandi è rivenditore autorizzato DEVELOP e propone il noleggio operativo di stampanti multifunzione professionali per aziende che vogliono dotarsi di attrezzature di stampa all\'avanguardia senza investimenti iniziali. Il canone mensile tutto incluso copre manutenzione, toner originali, assistenza tecnica e sostituzione dei componenti usurati, permettendo di avere sempre una macchina efficiente a costo prevedibile e deducibile fiscalmente.',
        features: [
          'Noleggio operativo di multifunzione DEVELOP (bianco/nero e colore)',
          'Canone tutto incluso: toner originali, manutenzione e assistenza tecnica on-site',
          'Installazione e configurazione in rete con tutti i pc dell\'ufficio',
          'Stampa da smartphone e tablet con Airprint e Google Cloud Print',
          'Scansione documenti verso email, cartelle di rete e sistemi documentali',
          'Sostituzione del dispositivo in caso di guasto irreparabile senza costi aggiuntivi'
        ],
        benefits: [
          'Nessun investimento iniziale: canone mensile fisso e totalmente deducibile',
          'Stampanti sempre efficienti con manutenzione e consumabili inclusi nel canone',
          'Assistenza tecnica rapida on-site con sostituzione garantita in caso di guasto',
          'Aggiornamento tecnologico al termine del contratto con modelli di ultima generazione'
        ]
      }
    },
    {
      icon: <Server className="w-12 h-12" />,
      title: 'Server a Noleggio',
      description: 'Il nostro team specializzato interviene sui server già presenti in azienda per aggiornamenti hardware mirati: aumento di memoria RAM, sostituzione o aggiunta di componenti per migliorare prestazioni e affidabilità. Progettiamo e forniamo anche nuovi server personalizzati, assemblati con componenti hardware di alta qualità e velocità elevata, dimensionati sulle reali esigenze della tua azienda. Offriamo inoltre noleggio operativo di server fisici, soluzione flessibile senza investimento iniziale, con manutenzione e supporto inclusi.',
      image: 'https://plus.unsplash.com/premium_photo-1764695543398-c9efa8689e4e?q=80&w=1074&auto=format&fit=crop',
      overlay: 15,
      details: {
        intro: 'Computerlandi progetta, fornisce e noleggia server fisici personalizzati per aziende che necessitano di potenza di calcolo e storage affidabili direttamente in sede. Dalle piccole aziende che necessitano di un primo server alle medie imprese con infrastrutture più complesse, dimensioniamo ogni soluzione sulle reali esigenze operative. Il noleggio operativo offre la massima flessibilità: hardware sempre aggiornato, manutenzione inclusa e nessun investimento iniziale elevato.',
        features: [
          'Progettazione e fornitura di server personalizzati con componenti di alta qualità',
          'Upgrade hardware di server esistenti: RAM, CPU, storage e alimentatori',
          'Noleggio operativo di server fisici con manutenzione e supporto inclusi',
          'Installazione di sistemi operativi server: Windows Server, Linux, VMware ESXi',
          'Configurazione RAID, storage NAS e soluzioni di alta disponibilità',
          'Monitoraggio remoto dello stato dell\'hardware e intervento preventivo'
        ],
        benefits: [
          'Hardware dimensionato perfettamente sulle esigenze attuali e future dell\'azienda',
          'Nessun investimento iniziale elevato con il noleggio operativo, canone deducibile',
          'Prestazioni elevate e affidabilità garantita con componenti enterprise selezionati',
          'Manutenzione e supporto inclusi per massima continuità operativa'
        ]
      }
    },
    {
      icon: <Layers className="w-12 h-12" />,
      title: 'Virtualizzazione',
      description: 'Virtualizziamo la tua infrastruttura IT con le migliori tecnologie del mercato: VMware, Proxmox e Citrix. Riduci i costi hardware, migliora la flessibilità e garantisci la continuità operativa.',
      image: 'https://plus.unsplash.com/premium_photo-1683134241482-4a3c44d2ab49?q=80&w=1170&auto=format&fit=crop',
      details: {
        intro: 'La virtualizzazione dell\'infrastruttura IT permette alle aziende di consolidare i server fisici, ridurre i costi hardware e migliorare significativamente la flessibilità operativa. Computerlandi implementa ambienti virtualizzati con le piattaforme leader del mercato — VMware vSphere, Proxmox VE e Citrix Hypervisor — progettando architetture scalabili e ad alta disponibilità che proteggono i workload critici aziendali.',
        features: [
          'Progettazione e implementazione di ambienti virtualizzati con VMware, Proxmox e Citrix',
          'Migrazione di server fisici verso macchine virtuali (P2V) con zero downtime',
          'Configurazione di cluster ad alta disponibilità (HA) e live migration',
          'Gestione centralizzata dell\'infrastruttura virtuale con dashboard unificata',
          'Ottimizzazione delle risorse: CPU, RAM e storage su ambienti multi-VM',
          'Integrazione con soluzioni di backup Veeam per protezione delle VM'
        ],
        benefits: [
          'Riduzione significativa dei costi hardware con consolidamento dei server fisici',
          'Alta disponibilità dei servizi con failover automatico in caso di guasto',
          'Flessibilità operativa con provisioning rapido di nuove macchine virtuali',
          'Disaster recovery semplificato grazie alla portabilità delle macchine virtuali'
        ]
      }
    }
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormStatus('sending');

    emailjs.send(
      EMAILJS_SERVICE_ID,
      EMAILJS_TEMPLATE_ID,
      {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
      },
      EMAILJS_PUBLIC_KEY
    ).then(() => {
      setFormStatus('success');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    }).catch(() => {
      setFormStatus('error');
    });
  };

  const HomePage = () => (
    <div>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-500 to-orange-600 text-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/2">
              <h1 className="text-5xl font-bold mb-6">Soluzioni IT Professionali per la Tua Azienda</h1>
              <p className="text-xl mb-8 text-orange-100">
                Computerlandi è il partner tecnologico che trasforma le sfide IT in opportunità di crescita.
              </p>
              <div className="flex gap-4 flex-wrap">
                <button
                  onClick={() => setCurrentPage('contact')}
                  className="bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-orange-50 transition"
                >
                  Contattaci
                </button>
                <button
                  onClick={() => setCurrentPage('services')}
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-orange-600 transition"
                >
                  I Nostri Servizi
                </button>
              </div>
            </div>
            <div className="md:w-1/2">
              <img
                src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80&fit=crop"
                alt="Ufficio IT professionale"
                className="rounded-2xl shadow-2xl w-full object-cover h-80"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">Perché Scegliere Computerlandi</h2>
          <div className="overflow-hidden">
            <div
              className="flex"
              style={{
                transform: `translateX(-${currentSlide * (isMobile ? 100 : 25)}%)`,
                transition: isTransitioning ? 'transform 2.5s ease-in-out' : 'none',
              }}
            >
              {[...features, ...features].map((feature, idx) => (
                <div key={idx} className="px-3" style={{ minWidth: isMobile ? '100%' : '25%' }}>
                  <div className="bg-white p-6 rounded-xl shadow-md text-center h-full">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 mx-auto">
                      <CheckCircle className="w-6 h-6 text-orange-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                    <p className="text-gray-600 text-sm">{feature.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">I Nostri Servizi</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, idx) => (
              <div key={idx} onClick={() => { setSelectedService(idx); setCurrentPage('services'); }} className="bg-white border-2 border-gray-100 rounded-xl hover:border-orange-500 hover:shadow-lg transition overflow-hidden cursor-pointer">
                <img src={service.image} alt={service.title} className="w-full h-40 object-cover" />
                <div className="p-6">
                  <div className="text-orange-600 mb-3">{service.icon}</div>
                  <h3 className="text-lg font-bold mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{service.description}</p>
                  <span className="text-orange-600 font-semibold text-sm inline-flex items-center gap-1">Scopri di più <ChevronRight className="w-4 h-4" /></span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <button 
              onClick={() => setCurrentPage('services')}
              className="text-orange-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
            >
              Scopri tutti i servizi <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-orange-500 to-orange-600 text-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Pronto a Digitalizzare la Tua Azienda?</h2>
          <p className="text-xl mb-8 text-orange-100">Contattaci per una consulenza gratuita</p>
          <button 
            onClick={() => setCurrentPage('contact')}
            className="bg-white text-orange-600 px-8 py-3 rounded-lg font-semibold hover:bg-orange-50 transition"
          >
            Richiedi Consulenza
          </button>
        </div>
      </section>
    </div>
  );

  const AboutPage = () => (
    <div className="py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-8 text-gray-800">Chi Siamo</h1>
        
        <div className="bg-orange-50 border-l-4 border-orange-600 p-6 mb-8 rounded-r-lg">
          <p className="text-xl text-gray-700 leading-relaxed">
            Computerlandi è un'azienda leader nel settore IT, specializzata in soluzioni tecnologiche innovative per le imprese.
          </p>
        </div>

        <div className="prose prose-lg max-w-none">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">La Nostra Storia</h2>
          <p className="text-gray-600 mb-6">
            Fondata con la missione di rendere la tecnologia accessibile e affidabile per ogni azienda, 
            Computerlandi si è affermata come partner di riferimento per centinaia di imprese in tutta Italia.
          </p>

          <h2 className="text-2xl font-bold mb-4 text-gray-800">La Nostra Missione</h2>
          <p className="text-gray-600 mb-6">
            Crediamo che la tecnologia debba essere un acceleratore di business, non un ostacolo. 
            Per questo offriamo soluzioni IT semplici, sicure e scalabili, accompagnando i nostri clienti 
            in ogni fase della trasformazione digitale.
          </p>

          <h2 className="text-2xl font-bold mb-4 text-gray-800">I Nostri Valori</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2 text-orange-600">Affidabilità</h3>
              <p className="text-gray-600">Partner solido su cui contare per la continuità del tuo business.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2 text-orange-600">Innovazione</h3>
              <p className="text-gray-600">Sempre aggiornati sulle ultime tecnologie e best practice del settore.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2 text-orange-600">Competenza</h3>
              <p className="text-gray-600">Team di professionisti certificati e costantemente formati.</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="font-bold text-lg mb-2 text-orange-600">Trasparenza</h3>
              <p className="text-gray-600">Comunicazione chiara e prezzi sempre ben definiti.</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-8 rounded-xl mt-10">
          <h3 className="text-2xl font-bold mb-4">Vuoi saperne di più?</h3>
          <p className="mb-6">Scopri come possiamo aiutare la tua azienda a crescere.</p>
          <button 
            onClick={() => setCurrentPage('contact')}
            className="bg-white text-orange-600 px-6 py-3 rounded-lg font-semibold hover:bg-orange-50 transition"
          >
            Contattaci
          </button>
        </div>
      </div>
    </div>
  );

  const ServicesPage = () => (
    <div className="py-16 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-bold mb-4 text-center text-gray-800">I Nostri Servizi</h1>
        <p className="text-xl text-gray-600 text-center mb-12 max-w-3xl mx-auto">
          Soluzioni IT complete per trasformare la tecnologia in vantaggio competitivo
        </p>

        <div className="space-y-8">
          {services.map((service, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition cursor-pointer"
              onClick={() => setSelectedService(idx)}
            >
              <div className="md:flex">
                <div className="md:w-1/4 relative overflow-hidden">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover min-h-48" />
                  <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: `rgba(234, 88, 12, ${(service.overlay ?? 25) / 100})` }}>
                    <div className="text-white">{service.icon}</div>
                  </div>
                </div>
                <div className="md:w-3/4 p-8">
                  <h3 className="text-2xl font-bold mb-3 text-gray-800">{service.title}</h3>
                  <p className="text-gray-600 mb-4">{service.description}</p>
                  <ul className="space-y-2 mb-6">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Analisi delle esigenze aziendali</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Implementazione e configurazione professionale</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">Supporto continuativo e manutenzione</span>
                    </li>
                  </ul>
                  <div className="flex items-center gap-6">
                    <button
                      onClick={(e) => { e.stopPropagation(); setCurrentPage('contact'); }}
                      className="text-orange-600 font-semibold inline-flex items-center gap-2 hover:gap-3 transition-all"
                    >
                      Richiedi informazioni <ChevronRight className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedService(idx); }}
                      className="text-gray-500 font-semibold hover:text-orange-600 transition-colors"
                    >
                      Scopri di più →
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gray-50 p-8 rounded-xl">
          <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">Servizi Aggiuntivi</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <h4 className="font-bold mb-2">Formazione</h4>
              <p className="text-gray-600 text-sm">Corsi per il tuo team su nuove tecnologie e strumenti</p>
            </div>
            <div className="text-center">
              <h4 className="font-bold mb-2">Consulenza</h4>
              <p className="text-gray-600 text-sm">Analisi IT e strategia digitale personalizzata</p>
            </div>
            <div className="text-center">
              <h4 className="font-bold mb-2">Gestione Progetti</h4>
              <p className="text-gray-600 text-sm">Project management per implementazioni complesse</p>
            </div>
            <div className="text-center">
              <h4 className="font-bold mb-2">Domini e Posta Aziendale</h4>
              <p className="text-gray-600 text-sm">Registrazione domini e posta professionale su Google Workspace o Microsoft Exchange</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const ContactPage = () => (
    <div className="py-16 px-6">
      <div className="max-w-5xl mx-auto">
        <h1 className="text-4xl font-bold mb-4 text-center text-gray-800">Contattaci</h1>
        <p className="text-xl text-gray-600 text-center mb-12">
          Siamo qui per rispondere alle tue domande e trovare la soluzione IT perfetta per te
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Informazioni di Contatto</h2>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold mb-1">Sede Principale</h3>
                  <p className="text-gray-600">Via Mure 99<br />31030 Altivole (TV), Italia</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold mb-1">Telefono</h3>
                  <p className="text-gray-600">0423 569315</p>
                  <p className="text-gray-600">0423 915726</p>
                  <p className="text-sm text-gray-500">Lun-Ven: 9:00 - 18:00</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-bold mb-1">Email</h3>
                  <p className="text-gray-600">info@computerlandi.it</p>
                  <p className="text-gray-600">assistenza@computerlandi.it</p>
                </div>
              </div>
            </div>

            <div className="mt-8 bg-orange-50 p-6 rounded-lg">
              <h3 className="font-bold mb-2 text-orange-900">Orari di Apertura</h3>
              <div className="space-y-1 text-sm text-gray-700">
                <p>Lunedì - Venerdì: 9:00 - 18:00</p>
                <p>Sabato: 9:00 - 13:00</p>
                <p>Domenica: Chiuso</p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-gray-800">Invia un Messaggio</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Nome e Cognome *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                  placeholder="Mario Rossi"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                  placeholder="mario.rossi@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Telefono</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                  placeholder="+39 123 456 7890"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Servizio di Interesse</label>
                <select
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                >
                  <option value="">Seleziona un servizio</option>
                  <option value="it">Soluzioni IT Aziendali</option>
                  <option value="cloud">Server e Cloud</option>
                  <option value="security">Cybersecurity</option>
                  <option value="support">Assistenza Tecnica</option>
                  <option value="other">Altro</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Messaggio *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none resize-none"
                  placeholder="Descrivi brevemente le tue esigenze..."
                ></textarea>
              </div>

              <button
                onClick={handleSubmit}
                disabled={formStatus === 'sending'}
                className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition disabled:opacity-60"
              >
                {formStatus === 'sending' ? 'Invio in corso...' : 'Invia Richiesta'}
              </button>

              {formStatus === 'success' && (
                <p className="text-green-600 text-sm text-center font-semibold">
                  Messaggio inviato! Ti risponderemo al più presto.
                </p>
              )}
              {formStatus === 'error' && (
                <p className="text-red-600 text-sm text-center font-semibold">
                  Errore nell'invio. Riprova o contattaci per telefono.
                </p>
              )}

              <p className="text-xs text-gray-500 text-center">
                * Campi obbligatori. I tuoi dati saranno trattati secondo la nostra privacy policy.
              </p>
            </div>
          </div>
        </div>

        {/* Google Maps */}
        <div className="mt-10">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Dove Siamo</h2>
          <div className="rounded-xl overflow-hidden shadow-lg">
            <iframe
              title="Computerlandi - Via Mure 99, Altivole (TV)"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1387.9!2d11.9609!3d45.7678!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x477954b0b0b0b0b1%3A0x0!2sVia+Mure+99%2C+31030+Altivole+TV%2C+Italia!5e0!3m2!1sit!2sit!4v1700000000000"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </div>
    </div>
  );

  const SupportPage = () => (
    <div className="py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-4 text-gray-800">Assistenza Remota</h1>
        <p className="text-xl text-gray-600 mb-12">
          Risolviamo i tuoi problemi informatici direttamente dal nostro ufficio, senza bisogno di una visita in sede.
        </p>

        {/* Come funziona */}
        <div className="bg-gray-50 rounded-xl p-8 mb-10">
          <h2 className="text-2xl font-bold mb-6 text-gray-800">Come funziona</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="font-bold mb-2">Scarica AnyDesk</h3>
              <p className="text-gray-600 text-sm">Scarica e avvia il programma AnyDesk sul tuo computer. Non è necessaria l'installazione.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="font-bold mb-2">Comunicaci il codice</h3>
              <p className="text-gray-600 text-sm">Aprendo AnyDesk vedrai un codice numerico. Comunicacelo telefonicamente o via email.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="font-bold mb-2">Accetti la connessione</h3>
              <p className="text-gray-600 text-sm">Quando ti connettiamo, ti verrà chiesto di autorizzare l'accesso. Clicca su "Accetta".</p>
            </div>
          </div>
        </div>

        {/* Download AnyDesk */}
        <div className="bg-white border-2 border-orange-500 rounded-xl p-8 mb-10 text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Download className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-gray-800">Scarica AnyDesk</h2>
          <p className="text-gray-600 mb-6">
            Clicca sul pulsante per scaricare AnyDesk sul tuo computer Windows. Il file è sicuro e non richiede installazione.
          </p>
          <a
            href="https://download.anydesk.com/AnyDesk.exe"
            className="inline-block bg-orange-600 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-orange-700 transition"
          >
            Scarica AnyDesk per Windows
          </a>
          <p className="text-xs text-gray-400 mt-4">Versione per Windows • Gratuito per uso personale</p>
        </div>

        {/* Contatti assistenza */}
        <div className="bg-orange-50 rounded-xl p-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Contattaci per l'assistenza</h2>
          <p className="text-gray-600 mb-6">Prima di scaricare AnyDesk, contattaci per concordare la sessione di assistenza remota.</p>
          <div className="flex flex-wrap gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Phone className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Telefono</p>
                <p className="text-gray-600">0423 569315 / 0423 915726</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                <Mail className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="font-semibold text-sm">Email</p>
                <p className="text-gray-600">assistenza@computerlandi.it</p>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <button
              onClick={() => setCurrentPage('contact')}
              className="bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-700 transition"
            >
              Apri un ticket di assistenza
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const CareersPage = () => {
    const [cvForm, setCvForm] = useState({ name: '', email: '', phone: '', role: '', message: '' });
    const [cvStatus, setCvStatus] = useState('idle');

    const handleCvChange = (e) => setCvForm({ ...cvForm, [e.target.name]: e.target.value });

    const handleCvSubmit = (e) => {
      e.preventDefault();
      setCvStatus('sending');
      emailjs.send(
        'service_ppzdu6p',
        'template_qoe42yn',
        {
          from_name: cvForm.name,
          from_email: cvForm.email,
          phone: cvForm.phone,
          service: `CANDIDATURA - ${cvForm.role}`,
          message: cvForm.message,
        },
        'Gas5dyJikfp7uT6jG'
      ).then(() => {
        setCvStatus('success');
        setCvForm({ name: '', email: '', phone: '', role: '', message: '' });
      }).catch(() => setCvStatus('error'));
    };

    return (
      <div className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold mb-4 text-gray-800">Lavora con Noi</h1>
          <p className="text-xl text-gray-600 mb-10">
            Sei un professionista IT e vuoi far parte di un team dinamico e in crescita? Siamo sempre alla ricerca di talenti.
          </p>

          {/* Profili ricercati */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-orange-500">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Cpu className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">Tecnico Informatico</h3>
              <p className="text-gray-600 text-sm">Assistenza hardware/software, installazione e manutenzione sistemi, supporto on-site e da remoto.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-orange-500">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">Sviluppatore</h3>
              <p className="text-gray-600 text-sm">Sviluppo applicazioni web e software aziendali, gestione database e integrazione sistemi.</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-md border-t-4 border-orange-500">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Server className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold mb-2">Senior IT</h3>
              <p className="text-gray-600 text-sm">Progettazione infrastrutture, gestione server, virtualizzazione, cloud e sicurezza informatica.</p>
            </div>
          </div>

          {/* Form candidatura */}
          <div className="bg-white p-8 rounded-xl shadow-lg">
            <h2 className="text-2xl font-bold mb-2 text-gray-800">Invia la tua Candidatura</h2>
            <p className="text-gray-500 text-sm mb-6">Compila il modulo e inviaci un messaggio. Allega il tuo CV via email a <span className="text-orange-600 font-semibold">info@computerlandi.it</span></p>
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Nome e Cognome *</label>
                  <input type="text" name="name" value={cvForm.name} onChange={handleCvChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    placeholder="Mario Rossi" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Email *</label>
                  <input type="email" name="email" value={cvForm.email} onChange={handleCvChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    placeholder="mario.rossi@example.com" />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Telefono</label>
                  <input type="tel" name="phone" value={cvForm.phone} onChange={handleCvChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none"
                    placeholder="+39 123 456 7890" />
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Ruolo di Interesse *</label>
                  <select name="role" value={cvForm.role} onChange={handleCvChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none">
                    <option value="">Seleziona un ruolo</option>
                    <option value="Tecnico Informatico">Tecnico Informatico</option>
                    <option value="Sviluppatore">Sviluppatore</option>
                    <option value="Senior IT">Senior IT</option>
                    <option value="Altro">Altro</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Presentati *</label>
                <textarea name="message" value={cvForm.message} onChange={handleCvChange} rows="4"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent outline-none resize-none"
                  placeholder="Raccontaci la tua esperienza e perché vorresti lavorare con noi..."></textarea>
              </div>
              <button onClick={handleCvSubmit} disabled={cvStatus === 'sending'}
                className="w-full bg-orange-600 text-white py-3 rounded-lg font-semibold hover:bg-orange-700 transition disabled:opacity-60">
                {cvStatus === 'sending' ? 'Invio in corso...' : 'Invia Candidatura'}
              </button>
              {cvStatus === 'success' && <p className="text-green-600 text-sm text-center font-semibold">Candidatura inviata! Ti contatteremo presto. Ricordati di inviarci il CV via email.</p>}
              {cvStatus === 'error' && <p className="text-red-600 text-sm text-center font-semibold">Errore nell'invio. Riprova o scrivici a info@computerlandi.it</p>}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const PrivacyPage = () => (
    <div className="py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-4 text-gray-800">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Ultimo aggiornamento: marzo 2026</p>

        <div className="space-y-10 text-gray-700">

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">1. Titolare del Trattamento</h2>
            <p>Il Titolare del trattamento dei dati personali è:</p>
            <div className="bg-gray-50 p-4 rounded-lg mt-3 text-sm">
              <p><strong>Computerlandi</strong></p>
              <p>Via Mure 99, 31030 Altivole (TV), Italia</p>
              <p>Email: info@computerlandi.it</p>
              <p>Tel: 0423 569315 / 0423 915726</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">2. Dati Raccolti</h2>
            <p className="mb-3">Il sito raccoglie le seguenti categorie di dati personali:</p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li><strong>Dati di contatto</strong>: nome, cognome, indirizzo email, numero di telefono, forniti volontariamente tramite il modulo di contatto o il modulo di candidatura.</li>
              <li><strong>Dati di navigazione</strong>: indirizzo IP, tipo di browser, pagine visitate, data e ora di accesso, raccolti automaticamente dai sistemi informatici del sito.</li>
              <li><strong>Cookie</strong>: file di testo salvati nel browser dell'utente per migliorare l'esperienza di navigazione (vedi sezione Cookie).</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">3. Finalità del Trattamento</h2>
            <p className="mb-3">I dati personali raccolti vengono utilizzati per:</p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li>Rispondere alle richieste di contatto e assistenza inviate tramite il modulo del sito.</li>
              <li>Gestire le candidature di lavoro ricevute.</li>
              <li>Migliorare il funzionamento e la sicurezza del sito web.</li>
              <li>Adempiere agli obblighi di legge.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">4. Base Giuridica del Trattamento</h2>
            <p className="text-sm">Il trattamento dei dati è basato sul consenso dell'utente (art. 6, par. 1, lett. a GDPR) per l'invio di comunicazioni, e sul legittimo interesse del titolare (art. 6, par. 1, lett. f GDPR) per la gestione delle richieste di contatto e per la sicurezza del sito.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">5. Servizi di Terze Parti</h2>
            <p className="mb-3 text-sm">Il sito utilizza i seguenti servizi di terze parti che potrebbero raccogliere dati:</p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li><strong>EmailJS</strong>: servizio per l'invio di email tramite moduli web. Privacy Policy: <a href="https://www.emailjs.com/legal/privacy-policy/" target="_blank" rel="noreferrer" className="text-orange-600 underline">emailjs.com</a></li>
              <li><strong>Google Maps</strong>: servizio di mappe integrato nella pagina Contatti. Privacy Policy: <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-orange-600 underline">policies.google.com</a></li>
              <li><strong>Unsplash</strong>: servizio di immagini utilizzato per le fotografie del sito. Privacy Policy: <a href="https://unsplash.com/privacy" target="_blank" rel="noreferrer" className="text-orange-600 underline">unsplash.com</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">6. Cookie Policy</h2>
            <p className="mb-3 text-sm">Il sito utilizza le seguenti tipologie di cookie:</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-orange-50">
                    <th className="text-left p-3 border border-gray-200">Tipo</th>
                    <th className="text-left p-3 border border-gray-200">Descrizione</th>
                    <th className="text-left p-3 border border-gray-200">Durata</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border border-gray-200 font-semibold">Necessari</td>
                    <td className="p-3 border border-gray-200">Indispensabili per il funzionamento del sito (preferenze cookie)</td>
                    <td className="p-3 border border-gray-200">Sessione / 12 mesi</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-3 border border-gray-200 font-semibold">Funzionali</td>
                    <td className="p-3 border border-gray-200">Permettono l'invio del modulo di contatto tramite EmailJS</td>
                    <td className="p-3 border border-gray-200">Sessione</td>
                  </tr>
                  <tr>
                    <td className="p-3 border border-gray-200 font-semibold">Terze Parti</td>
                    <td className="p-3 border border-gray-200">Impostati da Google Maps per il corretto funzionamento della mappa</td>
                    <td className="p-3 border border-gray-200">Variabile</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm">Puoi gestire le preferenze cookie in qualsiasi momento tramite il banner presente nel sito o dalle impostazioni del tuo browser.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">7. Conservazione dei Dati</h2>
            <p className="text-sm">I dati personali raccolti tramite i moduli di contatto vengono conservati per il tempo strettamente necessario a rispondere alla richiesta, e comunque non oltre 24 mesi. I dati di navigazione vengono conservati per un massimo di 12 mesi.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">8. Diritti dell'Utente</h2>
            <p className="mb-3 text-sm">In conformità al GDPR (Reg. UE 2016/679), l'utente ha il diritto di:</p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li><strong>Accesso</strong>: ottenere conferma del trattamento dei propri dati e riceverne copia.</li>
              <li><strong>Rettifica</strong>: correggere dati inesatti o incompleti.</li>
              <li><strong>Cancellazione</strong>: richiedere la cancellazione dei propri dati ("diritto all'oblio").</li>
              <li><strong>Limitazione</strong>: limitare il trattamento in determinate circostanze.</li>
              <li><strong>Portabilità</strong>: ricevere i propri dati in formato strutturato e leggibile.</li>
              <li><strong>Opposizione</strong>: opporsi al trattamento basato sul legittimo interesse.</li>
              <li><strong>Revoca del consenso</strong>: revocare in qualsiasi momento il consenso prestato.</li>
            </ul>
            <p className="mt-3 text-sm">Per esercitare questi diritti, scrivere a: <span className="text-orange-600 font-semibold">info@computerlandi.it</span></p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">9. Reclami</h2>
            <p className="text-sm">L'utente ha il diritto di proporre reclamo al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it) qualora ritenga che il trattamento dei propri dati violi il GDPR.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">10. Modifiche alla Privacy Policy</h2>
            <p className="text-sm">Il Titolare si riserva il diritto di modificare la presente Privacy Policy in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con aggiornamento della data in cima al documento.</p>
          </section>

        </div>
      </div>
    </div>
  );

  const CookiePolicyPage = () => (
    <div className="py-16 px-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold mb-4 text-gray-800">Cookie Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Ultimo aggiornamento: marzo 2026</p>

        <div className="space-y-10 text-gray-700">

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">1. Cosa sono i Cookie</h2>
            <p className="text-sm">I cookie sono piccoli file di testo che i siti web visitati dall'utente inviano al suo dispositivo, dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva. Grazie ai cookie, il sito ricorda le azioni e le preferenze dell'utente in modo che non debbano essere indicate nuovamente.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">2. Tipologie di Cookie Utilizzati</h2>

            <h3 className="text-lg font-semibold mb-2 text-orange-600">Cookie Tecnici Necessari</h3>
            <p className="text-sm mb-4">Questi cookie sono indispensabili per il corretto funzionamento del sito e non possono essere disattivati. Vengono impostati solo in risposta ad azioni dell'utente (es. impostazione delle preferenze cookie).</p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-orange-50">
                    <th className="text-left p-3 border border-gray-200">Nome</th>
                    <th className="text-left p-3 border border-gray-200">Finalità</th>
                    <th className="text-left p-3 border border-gray-200">Durata</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border border-gray-200 font-mono">cookieConsent</td>
                    <td className="p-3 border border-gray-200">Salva le preferenze sui cookie dell'utente</td>
                    <td className="p-3 border border-gray-200">12 mesi</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold mb-2 text-orange-600">Cookie Funzionali</h3>
            <p className="text-sm mb-4">Permettono al sito di ricordare le scelte effettuate dall'utente e di fornire funzionalità avanzate come l'invio del modulo di contatto.</p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-orange-50">
                    <th className="text-left p-3 border border-gray-200">Servizio</th>
                    <th className="text-left p-3 border border-gray-200">Finalità</th>
                    <th className="text-left p-3 border border-gray-200">Durata</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border border-gray-200 font-semibold">EmailJS</td>
                    <td className="p-3 border border-gray-200">Gestione invio moduli di contatto e candidatura</td>
                    <td className="p-3 border border-gray-200">Sessione</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-lg font-semibold mb-2 text-orange-600">Cookie di Terze Parti</h3>
            <p className="text-sm mb-4">Impostati da servizi esterni integrati nel sito. Questi cookie sono soggetti alle rispettive privacy policy dei fornitori.</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-orange-50">
                    <th className="text-left p-3 border border-gray-200">Servizio</th>
                    <th className="text-left p-3 border border-gray-200">Finalità</th>
                    <th className="text-left p-3 border border-gray-200">Privacy Policy</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border border-gray-200 font-semibold">Google Maps</td>
                    <td className="p-3 border border-gray-200">Visualizzazione mappa interattiva nella pagina Contatti</td>
                    <td className="p-3 border border-gray-200"><a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="text-orange-600 underline">policies.google.com</a></td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="p-3 border border-gray-200 font-semibold">Unsplash</td>
                    <td className="p-3 border border-gray-200">Caricamento immagini dei servizi</td>
                    <td className="p-3 border border-gray-200"><a href="https://unsplash.com/privacy" target="_blank" rel="noreferrer" className="text-orange-600 underline">unsplash.com/privacy</a></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">3. Come Gestire i Cookie</h2>
            <p className="text-sm mb-3">Puoi gestire le preferenze sui cookie in qualsiasi momento tramite:</p>
            <ul className="list-disc pl-6 space-y-2 text-sm">
              <li><strong>Banner cookie</strong>: il banner presente sul sito ti permette di accettare, rifiutare o personalizzare le tue preferenze.</li>
              <li><strong>Impostazioni del browser</strong>: puoi disabilitare i cookie direttamente dal tuo browser. Tieni presente che disabilitare alcuni cookie potrebbe compromettere il funzionamento del sito.</li>
            </ul>
            <div className="bg-gray-50 p-4 rounded-lg mt-4 text-sm space-y-1">
              <p><strong>Chrome</strong>: Impostazioni → Privacy e sicurezza → Cookie e altri dati dei siti</p>
              <p><strong>Firefox</strong>: Impostazioni → Privacy e sicurezza → Cookie e dati dei siti</p>
              <p><strong>Edge</strong>: Impostazioni → Cookie e autorizzazioni sito → Cookie e dati archiviati</p>
              <p><strong>Safari</strong>: Preferenze → Privacy → Gestisci dati siti web</p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">4. Modifiche alla Cookie Policy</h2>
            <p className="text-sm">Il Titolare si riserva il diritto di modificare la presente Cookie Policy in qualsiasi momento. Le modifiche saranno pubblicate su questa pagina con aggiornamento della data in cima al documento.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold mb-3 text-gray-800">5. Contatti</h2>
            <p className="text-sm">Per qualsiasi domanda relativa alla presente Cookie Policy, contattare il Titolare del trattamento:</p>
            <div className="bg-gray-50 p-4 rounded-lg mt-3 text-sm">
              <p><strong>Computerlandi</strong></p>
              <p>Via Mure 99, 31030 Altivole (TV), Italia</p>
              <p>Email: info@computerlandi.it</p>
              <p>Tel: 0423 569315 / 0423 915726</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );

  const ServiceDetailPage = () => {
    const service = services[selectedService];
    if (!service) return null;
    return (
      <div className="py-0">
        {/* Back button */}
        <div className="px-6 pt-8 pb-4 max-w-4xl mx-auto">
          <button
            onClick={() => { setSelectedService(null); setCurrentPage('services'); }}
            className="inline-flex items-center gap-2 text-orange-600 font-semibold hover:text-orange-700 transition mb-2"
          >
            ← Torna ai Servizi
          </button>
        </div>

        {/* Hero image */}
        <div className="w-full h-64 overflow-hidden">
          <img src={service.image} alt={service.title} className="w-full h-64 object-cover" />
        </div>

        <div className="max-w-4xl mx-auto px-6 py-10">
          {/* Icon and title */}
          <div className="flex items-center gap-4 mb-6">
            <div className="text-orange-600">{service.icon}</div>
            <h1 className="text-4xl font-bold text-gray-800">{service.title}</h1>
          </div>

          {/* Intro */}
          <p className="text-gray-600 text-lg leading-relaxed mb-10">{service.details.intro}</p>

          {/* Cosa offriamo */}
          <div className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Cosa offriamo</h2>
            <ul className="space-y-3">
              {service.details.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Vantaggi */}
          <div className="bg-orange-50 rounded-xl p-8 mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Vantaggi per la tua azienda</h2>
            <ul className="space-y-3">
              {service.details.benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <ChevronRight className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700 font-medium">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="text-center">
            <button
              onClick={() => { setSelectedService(null); setCurrentPage('contact'); }}
              className="bg-orange-600 text-white px-10 py-4 rounded-lg font-semibold text-lg hover:bg-orange-700 transition"
            >
              Richiedi Informazioni
            </button>
          </div>
        </div>
      </div>
    );
  };

  const pages = {
    home: HomePage(),
    about: AboutPage(),
    services: ServicesPage(),
    support: SupportPage(),
    careers: CareersPage(),
    privacy: PrivacyPage(),
    cookies: CookiePolicyPage(),
    contact: ContactPage(),
    serviceDetail: selectedService !== null ? ServiceDetailPage() : null
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => { setCurrentPage('home'); setSelectedService(null); }}>
              <img 
                src="/logo.png" 
                alt="Computerlandi Logo" 
                className="h-20"
              />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {navigation.map(item => (
                <button
                  key={item.id}
                  onClick={() => { setCurrentPage(item.id); setSelectedService(null); }}
                  className={`font-semibold transition ${
                    currentPage === item.id 
                      ? 'text-orange-600' 
                      : 'text-gray-600 hover:text-orange-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 pb-4 border-t pt-4">
              {navigation.map(item => (
                <button
                  key={item.id}
                  onClick={() => {
                    setCurrentPage(item.id);
                    setSelectedService(null);
                    setMobileMenuOpen(false);
                  }}
                  className={`block w-full text-left py-3 font-semibold transition ${
                    currentPage === item.id 
                      ? 'text-orange-600' 
                      : 'text-gray-600'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          )}
        </div>
      </header>

      {/* Page Content */}
      <main>
        {selectedService !== null && currentPage === 'services' ? pages.serviceDetail : pages[currentPage]}
      </main>

      <CookieBanner />

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src="/logo.png"
                alt="Computerlandi Logo"
                className="h-16 mb-4"
              />
              <p className="text-gray-400 text-sm">
                Il tuo partner IT di fiducia per soluzioni tecnologiche innovative.
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">Servizi</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="cursor-pointer hover:text-white" onClick={() => { setSelectedService(0); setCurrentPage('services'); }}>Soluzioni IT</li>
                <li className="cursor-pointer hover:text-white" onClick={() => { setSelectedService(1); setCurrentPage('services'); }}>Server e Cloud</li>
                <li className="cursor-pointer hover:text-white" onClick={() => { setSelectedService(2); setCurrentPage('services'); }}>Cybersecurity</li>
                <li className="cursor-pointer hover:text-white" onClick={() => { setSelectedService(3); setCurrentPage('services'); }}>Assistenza Tecnica</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Azienda</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="cursor-pointer hover:text-white" onClick={() => setCurrentPage('about')}>Chi Siamo</li>
                <li className="cursor-pointer hover:text-white" onClick={() => setCurrentPage('careers')}>Lavora con Noi</li>
                <li className="cursor-pointer hover:text-white" onClick={() => setCurrentPage('privacy')}>Privacy Policy</li>
                <li className="cursor-pointer hover:text-white" onClick={() => setCurrentPage('cookies')}>Cookie Policy</li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contatti</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>info@computerlandi.it</li>
                <li>0423 569315 / 0423 915726</li>
                <li>Via Mure 99, Altivole (TV)</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2024 Computerlandi. Tutti i diritti riservati.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}